<template>
    <div
        :class="pageRootClass"
    >
        <div
            v-if="variant !== 'page'"
            class="flex-none border-b border-gray-100 bg-white px-4 py-4"
        >
            <div class="mb-3 flex items-center justify-between">
                <div>
                    <h2 class="text-base font-bold text-gray-900">購物車</h2>
                    <p class="mt-1 text-xs text-gray-400 desk:text-dove-gray">{{ count }} 件商品</p>
                </div>
                <button
                    type="button"
                    class="flex h-9 items-center gap-1 rounded-full bg-gray-100 px-3 text-sm font-medium text-gray-600"
                    aria-label="關閉購物車"
                    @click="close"
                >
                    <XIcon class="h-5 w-5" />
                    <span>關閉</span>
                </button>
            </div>
            <button
                v-if="hasItems"
                type="button"
                class="text-xs font-bold text-red-500"
                @click="clear"
            >
                清空購物車
            </button>
        </div>

        <div
            v-if="variant === 'page' && !hasItems"
            class="flex min-h-[40vh] flex-1 flex-col items-center justify-center px-6 py-16 text-center desk:grid desk:min-h-0 desk:grid-cols-2 desk:items-stretch desk:justify-start desk:px-0 desk:py-0 desk:text-left"
        >
            <section class="desk:border-r desk:border-gray-100 desk:px-8 desk:py-8">
                <p class="hidden text-xs font-medium text-gray-400 desk:block desk:text-dove-gray">購物車</p>
                <h1 class="hidden desk:mt-2 desk:block desk:text-2xl desk:font-bold desk:text-gray-900">
                    購物車還沒有商品
                </h1>
                <p class="mb-5 text-sm font-medium text-gray-500 desk:mt-3 desk:mb-0 desk:font-normal desk:leading-6">
                    <span class="desk:hidden">購物車還沒有商品</span>
                    <span class="hidden desk:inline">從商城加入商品後，可在這裡查看與結算。</span>
                </p>
            </section>
            <section class="desk:flex desk:items-start desk:px-8 desk:py-8">
                <button
                    type="button"
                    class="btn px-6 text-sm desk:inline-flex desk:h-12 desk:w-full desk:max-w-[400px] desk:items-center desk:justify-center desk:text-base"
                    @click="goShop"
                >
                    前往購物
                </button>
            </section>
        </div>

        <div
            v-else-if="variant === 'page'"
            class="desktop-summary-layout flex min-h-0 flex-1 flex-col desk:grid desk:items-start"
        >
            <div class="min-w-0 bg-white desk:border-r desk:border-gray-100">
                <div class="flex items-center justify-between border-b border-gray-100 px-5 py-4 desk:px-8 desk:py-5">
                    <p class="text-sm font-bold text-gray-900">{{ count }} 件商品</p>
                    <button
                        type="button"
                        class="text-xs font-bold text-red-500 desk:hover:underline"
                        @click="clear"
                    >
                        清空購物車
                    </button>
                </div>
                <CartListItem
                    :amount="amount"
                    :origin_amount="origin_amount"
                    :items="items"
                    :count="count"
                    :hasItems="hasItems"
                    :offer="offer"
                />
            </div>
            <aside
                data-desktop-cart-summary
                class="desktop-summary hidden bg-white desk:block desk:px-6 desk:py-6"
            >
                <p class="text-sm font-bold text-gray-900">結算</p>
                <p class="mt-1 text-xs text-gray-400 desk:text-dove-gray">{{ count }} 件商品</p>
                <div class="mt-6 flex items-start justify-between gap-3">
                    <span class="text-xs font-medium text-gray-500">合計</span>
                    <div class="text-right text-primary">
                        <p
                            v-if="Number(origin_amount) > Number(amount)"
                            class="text-xs text-gray-400 line-through"
                        >
                            {{ currency }}{{ origin_amount | number_format }}
                        </p>
                        <div class="flex items-baseline justify-end">
                            <span class="text-sm font-bold">{{ currency }}</span>
                            <span class="ml-0.5 text-2xl font-black tracking-tight">
                                {{ amount | number_format }}
                            </span>
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    class="btn mt-6 flex h-12 w-full items-center justify-center text-base font-bold"
                    :disabled="!hasItems"
                    @click="checkout"
                >
                    去結算
                </button>
            </aside>
        </div>

        <div
            v-else
            :class="pageMainClass"
        >
            <div
                v-if="!hasItems"
                class="flex min-h-[40vh] flex-1 flex-col items-center justify-center px-6 py-16 text-center"
            >
                <p class="mb-5 text-sm font-medium text-gray-500">購物車還沒有商品</p>
                <button type="button" class="btn px-6 text-sm" @click="goShop">
                    前往購物
                </button>
            </div>
            <CartListItem
                v-else
                :amount="amount"
                :origin_amount="origin_amount"
                :items="items"
                :count="count"
                :hasItems="hasItems"
                :offer="offer"
            />
        </div>

        <div
            v-if="variant !== 'page' || hasItems"
            class="flex-none border-t border-gray-100 bg-white px-4 pt-3 desk:sticky desk:top-14 desk:border-y"
            :class="{ 'desk:hidden': variant === 'page' }"
            :style="
                variant === 'page'
                    ? undefined
                    : { paddingBottom: 'max(18px, env(safe-area-inset-bottom, 18px))' }
            "
        >
            <div class="flex items-center justify-between gap-4">
                <div class="min-w-0">
                    <p class="text-xs font-medium text-gray-400">合計</p>
                    <p class="mt-0.5 text-xl font-black text-primary">
                        {{ currency }}{{ amount | number_format }}
                    </p>
                    <p v-if="variant === 'page'" class="mt-1 text-xs text-gray-400">
                        {{ count }} 件商品
                    </p>
                </div>
                <button
                    type="button"
                    class="h-12 flex-none rounded-lg px-8 text-base font-bold text-white"
                    :class="{
                        'bg-primary': hasItems,
                        'bg-gray-200 text-gray-500 cursor-not-allowed': !hasItems,
                    }"
                    @click="checkout"
                >
                    去結算
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import CartListItem from "@/components/cart/CartListItem";
import { XIcon } from "@vue-hero-icons/outline";
import { mapGetters, mapActions } from "vuex";

export default {
    components: { CartListItem, XIcon },
    props: {
        variant: {
            type: String,
            default: "sheet",
        },
    },

    methods: {
        ...mapActions({
            clear: "cart/clear",
        }),

        close() {
            if (this.variant === "page") {
                this.$router.push(this.localePath({ name: "order" }));
                return;
            }
            this.$parent.close();
        },

        checkout() {
            if (!this.hasItems) return;
            if (this.variant !== "page") this.close();
            this.$router.push(this.localePath({ name: "order-checkout" }));
        },
        goShop() {
            this.$router.push(this.localePath({ name: "order" }));
        },
    },

    computed: {
        ...mapGetters({
            amount: "cart/amount",
            origin_amount: "cart/origin_amount",
            offer: "cart/offer",
            count: "cart/count",
            hasItems: "cart/hasItems",
            items: "cart/items",
        }),
        pageRootClass() {
            if (this.variant !== "page") {
                return "flex h-full max-h-[82vh] flex-col overflow-hidden bg-gray-50";
            }
            return "flex min-h-0 flex-1 flex-col bg-white pb-24 desk:pb-0";
        },
        pageMainClass() {
            if (this.variant !== "page") {
                return "min-h-0 flex-grow overflow-y-scroll bg-white";
            }
            if (!this.hasItems) {
                return "flex min-h-0 flex-1 flex-col bg-white";
            }
            return "min-w-0 bg-white";
        },
    },
};
</script>
